import React from 'react';
import { Box, Typography, Button, Chip, Badge } from '@mui/material';
import { Upload, FilterList, Download, Delete } from '@mui/icons-material';
import FileSearchBar from './FileSearchBar';
import FileViewModeToggle, { ViewMode } from './FileViewModeToggle';

interface FileToolbarProps {
  // 통계
  totalFiles: number;
  selectedCount: number;
  
  // 뷰 모드
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  
  // 검색
  onSearch: (searchTerm: string) => void;
  
  // 액션
  onUploadClick: () => void;
  onFilterClick?: () => void;
  
  // 일괄 작업 (NEW)
  onBulkDownload?: () => void;
  onBulkDelete?: () => void;
  
  // 필터 상태
  activeFilterCount?: number; // 활성화된 필터 개수
  
  // 선택된 파일 정보
  selectedSize?: string; // "2.5 MB" 형식
}

/**
 * 파일 관리 도구바
 * - 검색, 뷰 모드, 통계, 액션 버튼 통합
 * - 상단에 고정 배치
 * - 반응형 레이아웃
 */
const FileToolbar: React.FC<FileToolbarProps> = ({
  totalFiles,
  selectedCount,
  viewMode,
  onViewModeChange,
  onSearch,
  onUploadClick,
  onFilterClick,
  onBulkDownload,
  onBulkDelete,
  activeFilterCount,
  selectedSize,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* 첫 번째 줄: 통계 + 액션 버튼 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* 왼쪽: 통계 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" fontWeight="medium">
            전체 파일
          </Typography>
          <Chip
            label={totalFiles}
            size="small"
            color="primary"
            variant="filled"
          />
          
          {selectedCount > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                /
              </Typography>
              <Chip
                label={`${selectedCount}개 선택`}
                size="small"
                color="secondary"
                variant="filled"
              />
              {selectedSize && (
                <Typography variant="body2" color="text.secondary">
                  ({selectedSize})
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* 오른쪽: 액션 버튼 */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* 일괄 작업 버튼 (선택된 파일이 있을 때만 표시) */}
          {selectedCount > 0 && (
            <>
              {onBulkDownload && (
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={onBulkDownload}
                  size="small"
                  color="primary"
                >
                  다운로드 ({selectedCount})
                </Button>
              )}
              
              {onBulkDelete && (
                <Button
                  variant="outlined"
                  startIcon={<Delete />}
                  onClick={onBulkDelete}
                  size="small"
                  color="error"
                >
                  삭제 ({selectedCount})
                </Button>
              )}
            </>
          )}
          
          {onFilterClick && (
            <Badge 
              badgeContent={activeFilterCount} 
              color="primary"
              invisible={!activeFilterCount || activeFilterCount === 0}
            >
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={onFilterClick}
                size="small"
              >
                필터
              </Button>
            </Badge>
          )}
          
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={onUploadClick}
            size="small"
          >
            업로드
          </Button>
        </Box>
      </Box>

      {/* 두 번째 줄: 검색바 + 뷰 모드 토글 */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
        }}
      >
        {/* 검색바 (확장) */}
        <Box sx={{ flex: 1 }}>
          <FileSearchBar onSearch={onSearch} />
        </Box>

        {/* 뷰 모드 토글 */}
        <FileViewModeToggle
          value={viewMode}
          onChange={onViewModeChange}
        />
      </Box>
    </Box>
  );
};

export default FileToolbar;
